import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

type ClipboardPlatform = 'graia' | 'ryan';

interface ClipboardCard {
    front: string;
    back: string;
    supportContent?: string;
    references?: string;
    sourcePlatform: ClipboardPlatform;
}

type PastedCard = Omit<ClipboardCard, 'sourcePlatform'>;

interface ClipboardContextType {
    clipboardCards: ClipboardCard[];
    copyCard: (card: PastedCard) => Promise<void>;
    pasteCards: () => Promise<PastedCard[]>;
    clearClipboard: () => Promise<void>;
    hasClipboard: boolean;
    count: number;
    clipboardSourcePlatform: ClipboardPlatform | 'mixed' | null;
}

const ClipboardContext = createContext<ClipboardContextType | undefined>(undefined);

const authHeaders = (): HeadersInit => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('accessToken')}`
});

// Clipboard persistido no backend por usuário — NÃO usar sessionStorage/localStorage
// aqui: GRAIA e RYAN rodam em origens diferentes (ex: graia.com vs med.graia.com) e
// esses storages são isolados por origem no navegador, então um card copiado numa
// origem nunca apareceria na outra. A API é servida pelo mesmo backend nas duas
// origens, então persistir por userId é o único jeito de o clipboard atravessar.
export const ClipboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, loading: authLoading } = useAuth();
    const [clipboardCards, setClipboardCards] = useState<ClipboardCard[]>([]);

    const loadClipboard = useCallback(async () => {
        try {
            const res = await fetch('/api/clipboard', { headers: authHeaders() });
            if (res.ok) {
                const data = await res.json();
                setClipboardCards(Array.isArray(data.cards) ? data.cards : []);
            }
        } catch {
            // Falha ao carregar não deve travar a UI — clipboard fica vazio até a próxima tentativa
        }
    }, []);

    // Busca o clipboard assim que o usuário estiver autenticado (cobre tanto o
    // carregamento inicial quanto o momento logo após o login) e limpa localmente
    // ao deslogar, pra não vazar clipboard de uma conta pra outra no mesmo navegador.
    useEffect(() => {
        if (authLoading) return;
        if (isAuthenticated) {
            loadClipboard();
        } else {
            setClipboardCards([]);
        }
    }, [isAuthenticated, authLoading, loadClipboard]);

    const copyCard = async (card: PastedCard) => {
        const sourcePlatform: ClipboardPlatform =
            sessionStorage.getItem('graia_theme_context') === 'ryan' ? 'ryan' : 'graia';

        try {
            const res = await fetch('/api/clipboard', {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({
                    front: card.front,
                    back: card.back,
                    supportContent: card.supportContent || '',
                    references: card.references || '',
                    sourcePlatform
                })
            });
            if (res.ok) {
                const data = await res.json();
                setClipboardCards(Array.isArray(data.cards) ? data.cards : []);
            }
        } catch {
            // Sem rede: não atualiza o estado local pra não divergir do servidor
        }
    };

    // Retorna todos os cards copiados e limpa o clipboard (num único round-trip atômico)
    // sourcePlatform é só metadado de UI — não deve ir para o payload de criação de card
    const pasteCards = async (): Promise<PastedCard[]> => {
        try {
            const res = await fetch('/api/clipboard/paste', {
                method: 'POST',
                headers: authHeaders()
            });
            if (!res.ok) return [];

            const data = await res.json();
            const cards: ClipboardCard[] = Array.isArray(data.cards) ? data.cards : [];
            setClipboardCards([]);

            return cards.map(card => ({
                front: card.front,
                back: card.back,
                supportContent: card.supportContent,
                references: card.references
            }));
        } catch {
            return [];
        }
    };

    const clearClipboard = async () => {
        setClipboardCards([]);
        try {
            await fetch('/api/clipboard', { method: 'DELETE', headers: authHeaders() });
        } catch {
            // Falha silenciosa — o próximo loadClipboard reconcilia com o servidor
        }
    };

    const clipboardSourcePlatform: ClipboardPlatform | 'mixed' | null = (() => {
        if (clipboardCards.length === 0) return null;
        const platforms = new Set(clipboardCards.map(c => c.sourcePlatform));
        if (platforms.size > 1) return 'mixed';
        return clipboardCards[0].sourcePlatform;
    })();

    return (
        <ClipboardContext.Provider value={{
            clipboardCards,
            copyCard,
            pasteCards,
            clearClipboard,
            hasClipboard: clipboardCards.length > 0,
            count: clipboardCards.length,
            clipboardSourcePlatform
        }}>
            {children}
        </ClipboardContext.Provider>
    );
};

export const useClipboard = () => {
    const context = useContext(ClipboardContext);
    if (!context) {
        throw new Error('useClipboard deve ser usado dentro de ClipboardProvider');
    }
    return context;
};
